/**
 * ReviewPossibleDuplicatesDialog
 *
 * Admin tool for cleaning up historical near-duplicate quotations.
 * Lists every (older, newer) pair the silent read-time dedupe currently
 * considers a duplicate. For each pair, the admin can:
 *
 *   • Replace old with new  → POST /quotations/{older}/supersede
 *                             {superseded_by: newer.id}
 *   • Keep both (distinct)  → POST /quotations/{older}/mark-distinct
 *                             (sets force_separate so the silent dedupe
 *                              never collapses them again)
 *
 * The action runs on the OLDER row in each pair — that's the one the
 * staff probably saved by accident before adopting the new at-save-time
 * duplicate prompt.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { api, formatINR } from '../../lib/store';
import { toast } from 'sonner';
import { GitMerge, ArrowLeftRight, RefreshCw, Loader2, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function MiniQuoteCard({ q, accent }) {
  const items = q?.items_preview || [];
  return (
    <Card className={`p-2 border ${accent === 'red' ? 'border-red-500/40 bg-red-50/40' : 'border-emerald-500/40 bg-emerald-50/40'}`}>
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <FileText className="w-3 h-3 text-[#5b6450]" />
            <span className="font-semibold text-xs truncate">{q?.quotation_number || '—'}</span>
          </div>
          <p className="text-[10px] text-[#5b6450]">{fmtDate(q?.created_at)} · {q?.created_by_name || 'Unknown'}</p>
        </div>
        <p className="text-xs font-semibold shrink-0">{formatINR(q?.grand_total || 0)}</p>
      </div>
      <ul className="mt-1.5 space-y-0.5 text-[10px] leading-tight">
        {items.slice(0, 5).map((it, i) => (
          <li key={`${it.product_code || ''}-${it.description || ''}-${i}`} className="flex justify-between gap-2">
            <span className="truncate">{it.description || '—'}{it.quantity ? ` × ${it.quantity}${it.unit ? ' ' + it.unit : ''}` : ''}</span>
            {it.amount ? <span className="shrink-0 text-[#1f2a16]">{formatINR(it.amount)}</span> : null}
          </li>
        ))}
        {items.length > 5 && <li className="italic text-[#8F9485]">+ {items.length - 5} more…</li>}
      </ul>
    </Card>
  );
}

export default function ReviewPossibleDuplicatesDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [pairs, setPairs] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const fetchPairs = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listPossibleDuplicates();
      setPairs(res.data.pairs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load duplicates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchPairs();
  }, [open, fetchPairs]);

  // "Replace" — supersede the OLDER row (loser) with the NEWER row (winner).
  const handleReplace = async (pair) => {
    setBusyId(pair.loser.id);
    try {
      await api.supersedeQuotation(pair.loser.id, pair.winner.id);
      toast.success(`Hid ${pair.loser.quotation_number} (merged into ${pair.winner.quotation_number}).`);
      setPairs((p) => p.filter((x) => x.loser.id !== pair.loser.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Replace failed');
    } finally {
      setBusyId(null);
    }
  };

  // "Keep both" — flag the older as force_separate so the silent dedupe
  // never tries to merge them again.
  const handleKeepBoth = async (pair) => {
    setBusyId(pair.loser.id);
    try {
      await api.markQuotationDistinct(pair.loser.id);
      toast.success(`Confirmed both ${pair.loser.quotation_number} and ${pair.winner.quotation_number} are distinct.`);
      setPairs((p) => p.filter((x) => x.loser.id !== pair.loser.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mark-distinct failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl bg-[#F5F2E8] border-[#1f2a16]/20"
        data-testid="review-duplicates-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1f2a16]">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Review possible duplicate quotations
          </DialogTitle>
          <DialogDescription className="text-[#5b6450]">
            Historical pairs flagged by the dedupe engine in the last 90 days. Pick an action per pair —
            <strong> Replace</strong> hides the older row from analytics; <strong>Keep both</strong>
            tells the system they&apos;re genuinely different orders.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 -mt-1">
          <p className="text-xs text-[#5b6450]">
            {loading ? 'Scanning…' : `${pairs.length} pair${pairs.length === 1 ? '' : 's'} found`}
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchPairs}
            disabled={loading}
            data-testid="refresh-dup-pairs-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Rescan
          </Button>
        </div>

        <div className="max-h-[65vh] overflow-y-auto pr-1 space-y-3">
          {!loading && pairs.length === 0 && (
            <div className="text-center py-8 text-[#5b6450]">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-600" />
              <p className="text-sm font-medium">No duplicate pairs found.</p>
              <p className="text-xs">Analytics is clean.</p>
            </div>
          )}
          {pairs.map((pair) => {
            const busy = busyId === pair.loser.id;
            return (
              <Card
                key={pair.loser.id}
                className="p-3 border border-[#1f2a16]/15 bg-white"
                data-testid={`dup-pair-${pair.loser.id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[#1f2a16]">{pair.winner.customer_name || 'Unknown customer'}</span>
                    <Badge variant="outline" className="text-[10px] py-0 px-1 border-amber-600/50 text-amber-700">
                      {Math.round((pair.overlap_score || 0) * 100)}% match
                    </Badge>
                  </div>
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleKeepBoth(pair)}
                      disabled={busy}
                      className="border-emerald-700 text-emerald-800 hover:bg-emerald-50 h-8 text-xs"
                      data-testid={`keep-both-${pair.loser.id}`}
                    >
                      <ArrowLeftRight className="w-3 h-3 mr-1" />
                      Keep both
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleReplace(pair)}
                      disabled={busy}
                      className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
                      data-testid={`replace-${pair.loser.id}`}
                    >
                      <GitMerge className="w-3 h-3 mr-1" />
                      Hide older
                    </Button>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-red-700 mb-1">Older (will be hidden)</p>
                    <MiniQuoteCard q={pair.loser} accent="red" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-emerald-700 mb-1">Newer (kept)</p>
                    <MiniQuoteCard q={pair.winner} accent="green" />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
